import { useState, type FormEvent, type KeyboardEvent } from "react";
import { GESTALTUNGSFELDER, type InqaForm } from "./state";

interface Props {
  form: InqaForm;
  loading: boolean;
  onChange: (patch: Partial<InqaForm>) => void;
  onSubmit: () => void;
}

export function Step1Form({ form, loading, onChange, onSubmit }: Props) {
  const [rolleInput, setRolleInput] = useState("");

  function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (loading) return;
    if (form.gestaltungsfelder.length === 0) {
      window.alert("Bitte mindestens ein Gestaltungsfeld auswählen.");
      return;
    }
    onSubmit();
  }

  function toggleGestaltungsfeld(value: number) {
    const next = form.gestaltungsfelder.includes(value)
      ? form.gestaltungsfelder.filter((n) => n !== value)
      : [...form.gestaltungsfelder, value].sort((a, b) => a - b);
    onChange({ gestaltungsfelder: next });
  }

  function commitRolle() {
    const trimmed = rolleInput.trim().replace(/,$/, "");
    if (!trimmed) return;
    if (form.rollen.includes(trimmed)) {
      setRolleInput("");
      return;
    }
    onChange({ rollen: [...form.rollen, trimmed] });
    setRolleInput("");
  }

  function handleRolleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      commitRolle();
    }
  }

  function removeRolle(idx: number) {
    onChange({ rollen: form.rollen.filter((_, i) => i !== idx) });
  }

  return (
    <section className="wizard-section" data-inqa-step="1">
      <h2 className="form-title">INQA: Projektdaten eingeben</h2>
      <p className="form-description">
        Gib die Projektdetails ein. Der Agent generiert daraus eine vollständige Projektreferenz.
      </p>
      <form className="report-form" onSubmit={submit}>
        <div className="form-row">
          <label htmlFor="inqa-website-url">
            Website-URL <span className="field-hint">(optional)</span>
          </label>
          <input
            id="inqa-website-url"
            type="url"
            placeholder="https://…"
            value={form.websiteUrl}
            onChange={(e) => onChange({ websiteUrl: e.target.value })}
          />
        </div>

        <div className="form-row">
          <label htmlFor="inqa-projekttitel">Projekttitel *</label>
          <input
            id="inqa-projekttitel"
            type="text"
            required
            placeholder="z. B. Weiterentwicklung der Beschaffungsorganisation"
            value={form.projekttitel}
            onChange={(e) => onChange({ projekttitel: e.target.value })}
          />
        </div>

        <div className="form-row">
          <label htmlFor="inqa-branche">Branche des Kundenunternehmens *</label>
          <input
            id="inqa-branche"
            type="text"
            required
            placeholder="z. B. Maschinen- und Anlagenbau"
            value={form.branche}
            onChange={(e) => onChange({ branche: e.target.value })}
          />
        </div>

        <div className="form-row">
          <label htmlFor="inqa-mitarbeitende">Mitarbeitende (ca.) *</label>
          <input
            id="inqa-mitarbeitende"
            type="text"
            required
            placeholder="z. B. 200"
            value={form.mitarbeitende}
            onChange={(e) => onChange({ mitarbeitende: e.target.value })}
          />
        </div>

        <div className="form-inline-group">
          <div className="form-row form-row-inline">
            <label htmlFor="inqa-personentage">Personentage (PT) *</label>
            <input
              id="inqa-personentage"
              type="number"
              required
              min={1}
              placeholder="z. B. 10"
              value={form.personentage}
              onChange={(e) => onChange({ personentage: e.target.value })}
            />
          </div>
          <div className="form-row form-row-inline">
            <label htmlFor="inqa-stunden-pro-tag">Stunden/Tag</label>
            <input
              id="inqa-stunden-pro-tag"
              type="number"
              min={1}
              max={10}
              placeholder="8"
              value={form.stundenProTag}
              onChange={(e) => onChange({ stundenProTag: e.target.value })}
            />
          </div>
        </div>

        <div className="form-inline-group">
          <div className="form-row form-row-inline">
            <label htmlFor="inqa-zeitraum-start">Zeitraum Start (MM/YYYY) *</label>
            <input
              id="inqa-zeitraum-start"
              type="text"
              required
              placeholder="z. B. 04/2024"
              pattern="\d{2}/\d{4}"
              value={form.zeitraumStart}
              onChange={(e) => onChange({ zeitraumStart: e.target.value })}
            />
          </div>
          <div className="form-row form-row-inline">
            <label htmlFor="inqa-zeitraum-end">Zeitraum Ende (MM/YYYY) *</label>
            <input
              id="inqa-zeitraum-end"
              type="text"
              required
              placeholder="z. B. 06/2024"
              pattern="\d{2}/\d{4}"
              value={form.zeitraumEnd}
              onChange={(e) => onChange({ zeitraumEnd: e.target.value })}
            />
          </div>
        </div>

        <div className="form-row form-row-full">
          <label htmlFor="inqa-kontaktdaten">Kontaktdaten des Unternehmens</label>
          <textarea
            id="inqa-kontaktdaten"
            rows={4}
            placeholder={"Einfach reinkopieren – die KI formatiert es automatisch. z. B.:\nMustermann GmbH\nMax Mustermann, Geschäftsführer\nmax@mustermann.de\n+49 123 456789\nMusterstraße 1, 12345 Musterstadt"}
            value={form.kontaktdaten}
            onChange={(e) => onChange({ kontaktdaten: e.target.value })}
          />
          <small className="form-hint">
            Ansprechpartner, E-Mail, Telefon, Adresse – beliebiges Format, wird automatisch strukturiert.
          </small>
        </div>

        <div className="form-row form-row-full">
          <label>Betriebliche Gestaltungsfelder (mind. 1 wählen) *</label>
          <div className="gestaltungsfelder-group">
            {GESTALTUNGSFELDER.map((opt) => (
              <label className="gestaltungsfeld-item" key={opt.value}>
                <input
                  type="checkbox"
                  checked={form.gestaltungsfelder.includes(opt.value)}
                  onChange={() => toggleGestaltungsfeld(opt.value)}
                />
                {opt.label}
              </label>
            ))}
          </div>
        </div>

        <p className="form-hint form-hint-block">
          Jeder Abschnitt wird auf max. 900 Zeichen begrenzt (INQA-Portal-Limit: 1000 Zeichen).
        </p>

        <div className="form-row form-row-full">
          <label htmlFor="inqa-ausgangssituation">Ausgangssituation – Stichpunkte *</label>
          <textarea
            id="inqa-ausgangssituation"
            rows={4}
            required
            placeholder="Was war die Ausgangslage? Welche Herausforderungen gab es? Warum war das Projekt nötig?"
            value={form.ausgangssituation}
            onChange={(e) => onChange({ ausgangssituation: e.target.value })}
          />
        </div>

        <div className="form-row form-row-full">
          <label htmlFor="inqa-zielsetzung">Zielsetzung – Stichpunkte *</label>
          <textarea
            id="inqa-zielsetzung"
            rows={3}
            required
            placeholder="Was sollte erreicht werden? Konkrete Projektziele."
            value={form.zielsetzung}
            onChange={(e) => onChange({ zielsetzung: e.target.value })}
          />
        </div>

        <div className="form-row form-row-full">
          <label htmlFor="inqa-kerninhalte">Kerninhalte – Stichpunkte</label>
          <textarea
            id="inqa-kerninhalte"
            rows={3}
            placeholder="Welche Themen wurden bearbeitet? (optional – Agent ergänzt aus Zielsetzung)"
            value={form.kerninhalte}
            onChange={(e) => onChange({ kerninhalte: e.target.value })}
          />
        </div>

        <div className="form-row form-row-full">
          <label htmlFor="inqa-methoden">Vorgehen / Methoden – Stichpunkte</label>
          <textarea
            id="inqa-methoden"
            rows={3}
            placeholder="Welche Methoden wurden eingesetzt? (optional – Agent wählt passende Methoden)"
            value={form.methoden}
            onChange={(e) => onChange({ methoden: e.target.value })}
          />
        </div>

        <div className="form-row form-row-full">
          <label htmlFor="inqa-ergebnisse">Ergebnisse – Stichpunkte</label>
          <textarea
            id="inqa-ergebnisse"
            rows={3}
            placeholder="Was wurde erreicht? Wichtigste Projektergebnisse. (optional)"
            value={form.ergebnisse}
            onChange={(e) => onChange({ ergebnisse: e.target.value })}
          />
        </div>

        <div className="form-row form-row-full">
          <label htmlFor="inqa-rollen-input">Beteiligte Rollen</label>
          <div className="tags-input-container">
            {form.rollen.map((rolle, i) => (
              <span className="tag-chip" key={`${rolle}-${i}`}>
                {rolle}
                <button
                  type="button"
                  title="Entfernen"
                  onClick={() => removeRolle(i)}
                >
                  ×
                </button>
              </span>
            ))}
            <input
              id="inqa-rollen-input"
              type="text"
              className="tags-input"
              placeholder="Rolle eingeben, Enter drücken…"
              value={rolleInput}
              onChange={(e) => setRolleInput(e.target.value)}
              onKeyDown={handleRolleKeyDown}
              onBlur={commitRolle}
            />
          </div>
        </div>

        <div className="form-row">
          <label htmlFor="inqa-coach-name">Coach-Name</label>
          <input
            id="inqa-coach-name"
            type="text"
            placeholder="Dein Name als Coach"
            value={form.coachName}
            onChange={(e) => onChange({ coachName: e.target.value })}
          />
        </div>

        <div className="form-row">
          <label htmlFor="inqa-coach-unternehmen">Coach-Unternehmen</label>
          <input
            id="inqa-coach-unternehmen"
            type="text"
            placeholder="Dein Beratungsunternehmen"
            value={form.coachUnternehmen}
            onChange={(e) => onChange({ coachUnternehmen: e.target.value })}
          />
        </div>

        <div className="form-actions">
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? "Wird geladen…" : "Projektreferenz generieren"}
          </button>
        </div>
      </form>
    </section>
  );
}
